// ============================================================================
// RATE WORKSHEET (Arrival Changes 1.5c / CRS-CheckIn-Amend-Change-Request §9)
// ============================================================================
// The pricing lane for change items in the Booking Changes queue. Opens from
// an ARRIVAL_CHANGE / FO_CHANGE_REQUEST row, shows the booking's lines with
// rack vs agreed per component (same shape the booking form and walk-in use),
// and saves through PUT /Booking/{id}/rate-worksheet — which works on
// in-house and departed bookings the full edit screen refuses.
//
// Save & Resolve chains the linked approval's decision (with the response
// note) so pricing and closing the work item are one motion, not two screens.
//
// Lifecycle-modal pattern: inline state reset on [isOpen, approval], no
// minimize, no dirty plumbing — the worksheet is opened to be finished.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { approvalService } from 'api/services/approvalService';
import { bookingService } from 'api/services/bookingService';
import { checkInService } from 'api/services/checkInService';
import { configService } from 'api/services/configService';
import notificationService from 'services/notificationService';
import type { Approval } from 'api/types/approval.types';
import { requestedBasisLabel } from 'api/types/approval.types';
import type { BookingAdjustmentRequest, BookingFull, RateWorksheetDiscount } from 'api/types/booking.types';
import type { CheckIn } from 'api/types/checkin.types';
import { formatCurrency } from 'components/modals/modules/booking-module/booking.utils';

interface RateWorksheetModalProps {
  isOpen: boolean;
  /** The change item being worked — supplies bookingId, request text and the resolve target. */
  approval: Approval | null;
  /** RBAC 'approve' — enables Save & Resolve (Save alone only needs the worksheet endpoint). */
  canApprove?: boolean;
  onClose: () => void;
  /** Fired after a successful save (and/or resolve) so the queue refetches. */
  onApplied: () => void;
}

interface LineEdit {
  room: string;
  meal: string;
  extra: string;
  remarks: string;
}

const num = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const RateWorksheetModal: React.FC<RateWorksheetModalProps> = ({
  isOpen, approval, canApprove = false, onClose, onApplied,
}) => {
  const [full, setFull] = useState<BookingFull | null>(null);
  /** The check-in record — the ACTUALS the worksheet prices against. Null pre-arrival. */
  const [checkIn, setCheckIn] = useState<CheckIn | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lineEdits, setLineEdits] = useState<LineEdit[]>([]);
  const [bookingType, setBookingType] = useState<'Percentage' | 'FlatAmount'>('FlatAmount');
  const [bookingValue, setBookingValue] = useState('');
  const [bookingRemarks, setBookingRemarks] = useState('');
  // Overall-discount editor — sent as an override ONLY when the user touches
  // it; otherwise the stored discount stands (recomputed server-side).
  const [discType, setDiscType] = useState<'' | 'Percentage' | 'FlatAmount'>('');
  const [discValue, setDiscValue] = useState('');
  const [discRemarks, setDiscRemarks] = useState('');
  const [discTouched, setDiscTouched] = useState(false);
  const [responseNote, setResponseNote] = useState('');
  const [error, setError] = useState('');
  /** Property no-show policy (NO_SHOW_POLICY config) — hint only; the human decides. */
  const [noShowPolicy, setNoShowPolicy] = useState<{ basis: string; percent: number } | null>(null);

  // ── Load + prefill from the STORED adjustments ──────────────────────────
  useEffect(() => {
    if (!isOpen || !approval?.bookingId) return;
    let cancelled = false;
    setLoading(true);
    setFull(null);
    setError('');
    setResponseNote('');
    (async () => {
      try {
        const [res, ci] = await Promise.all([
          bookingService.getFullBooking(approval.bookingId!),
          // Null pre-arrival — the actuals panel simply doesn't render then.
          checkInService.getByBooking(approval.bookingId!).catch(() => null),
        ]);
        const data = res.data;
        if (cancelled || !data) return;
        setFull(data);
        setCheckIn(ci);

        // Policy hint for no-show pricing — property-scoped, non-fatal.
        setNoShowPolicy(null);
        if ((data.detailLines ?? []).some((l) => (l.noShowRooms ?? 0) > 0)) {
          configService.getEffectiveConfig('NO_SHOW_POLICY',
            [{ type: 'PROPERTY', id: data.booking.propertyId }])
            .then((cfg) => {
              if (cancelled) return;
              const vals = cfg.data ?? [];
              const basis = vals.find((v) => v.configKey === 'noshow.charge_basis')?.effectiveValue ?? 'FULL_STAY';
              const percent = Number(vals.find((v) => v.configKey === 'noshow.retention_percent')?.effectiveValue ?? '100');
              setNoShowPolicy({ basis, percent: Number.isFinite(percent) ? percent : 100 });
            })
            .catch(() => { /* hint only */ });
        }
        setDiscType((data.booking.discountType as '' | 'Percentage' | 'FlatAmount') ?? '');
        setDiscValue(data.booking.discountValue != null ? String(data.booking.discountValue) : '');
        setDiscRemarks(data.booking.discountRemarks ?? '');
        setDiscTouched(false);

        const adj = data.adjustments ?? [];
        setLineEdits((data.detailLines ?? []).map((line) => {
          const forLine = (code: string) => adj.find(
            (a) => a.bookingDetailId === line.bookingDetailId
              && a.componentCode === code && a.adjustmentType === 'AgreedRate');
          const remarks = adj.find(
            (a) => a.bookingDetailId === line.bookingDetailId && a.remarks)?.remarks ?? '';
          return {
            room: forLine('ROOM')?.agreedUnitRate != null ? String(forLine('ROOM')!.agreedUnitRate) : '',
            meal: forLine('MEAL')?.agreedUnitRate != null ? String(forLine('MEAL')!.agreedUnitRate) : '',
            extra: forLine('EXTRA_PERSON')?.agreedUnitRate != null ? String(forLine('EXTRA_PERSON')!.agreedUnitRate) : '',
            remarks,
          };
        }));

        const bookingRow = adj.find((a) => a.componentCode === 'BOOKING');
        setBookingType(bookingRow?.adjustmentType === 'Percentage' ? 'Percentage' : 'FlatAmount');
        setBookingValue(bookingRow ? String(bookingRow.adjustmentValue) : '');
        setBookingRemarks(bookingRow?.remarks ?? '');
      } catch {
        if (!cancelled) setError('Failed to load the booking.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, approval]);

  const booking = full?.booking ?? null;
  const nights = Math.max(1, booking?.numNights ?? 1);
  const totalNoShow = full?.detailLines.reduce((a, l) => a + (l.noShowRooms ?? 0), 0) ?? 0;
  const noShowPolicyLabel = noShowPolicy
    ? ({
        FIRST_NIGHT: 'charge the first night only',
        PERCENT: `charge ${noShowPolicy.percent}% retention`,
        NONE: 'no charge (waive)',
        FULL_STAY: 'charge the full booked amount',
      } as Record<string, string>)[noShowPolicy.basis] ?? noShowPolicy.basis
    : null;
  const isPerPax = (full?.commercialLines?.length ?? 0) > 0;
  const subtotal = booking?.subtotal ?? 0;

  // ── Booked vs ARRIVED rooms per line. Assigned rooms are matched to lines
  // by the line's EFFECTIVE category (hosted rooms count against the hosting
  // line) — the same rule the check-in reconciliation uses. ─────────────────
  const arrivedByLine = useMemo(() => {
    if (!full || !checkIn) return null;
    const counts = new Map<string, number>();
    checkIn.rooms.forEach((r) => counts.set(r.roomCategoryId, (counts.get(r.roomCategoryId) ?? 0) + 1));
    return full.detailLines.map((line) => {
      const eff = line.allocatedRoomCategoryId ?? line.roomCategoryId;
      const have = counts.get(eff) ?? 0;
      const take = Math.min(line.numberOfRooms, have);
      counts.set(eff, have - take);
      return take;
    });
  }, [full, checkIn]);

  // ── Rack recalculated at ACTUALS — advisory only. Rooms are exact per
  // line; meals scale with the on-site adult count, which is booking-level
  // (check-in pax is not captured per line), so that part is an honest
  // approximation and labelled as such. ────────────────────────────────────
  const actualsRecalc = useMemo(() => {
    if (!full || !booking || !checkIn || !arrivedByLine || isPerPax) return null;
    let bookedRooms = 0;
    let actualRooms = 0;
    full.detailLines.forEach((line, i) => {
      const rackLine = line.totalRoomCharges ?? (line.ratePerRoom * line.numberOfRooms * nights);
      bookedRooms += rackLine;
      actualRooms += line.numberOfRooms > 0 ? (rackLine / line.numberOfRooms) * arrivedByLine[i] : 0;
    });
    const bookedMeal = full.detailLines.reduce((a, l) => a + (l.totalMealCharges ?? 0), 0);
    const bookedAdults = booking.numAdults ?? 0;
    const actualAdults = checkIn.numAdults ?? bookedAdults;
    const actualMeal = bookedAdults > 0 ? (bookedMeal * actualAdults) / bookedAdults : bookedMeal;
    const bookedRack = bookedRooms + bookedMeal;
    const actualRack = actualRooms + actualMeal;
    const gap = Math.round((bookedRack - actualRack) * 100) / 100;
    return { bookedRack, actualRack, gap, bookedAdults, actualAdults };
  }, [full, booking, checkIn, arrivedByLine, nights, isPerPax]);

  // ── Client-side estimate of the new figures (server recomputes on save;
  // the same formulas, so the preview and the saved result agree) ─────────
  const estimate = useMemo(() => {
    if (!full || !booking) return null;
    let adjTotal = 0;
    if (!isPerPax) {
      full.detailLines.forEach((line, i) => {
        const e = lineEdits[i];
        if (!e) return;
        /* Quantity = stored charge / rack rate, mirroring the server's Units()
           fallback for lines rebuilt from stored rows (BookingService). This
           worksheet sends only the agreed RATE — the server derives the quantity —
           so anything else computed here makes the preview disagree with what is
           actually saved, which is precisely what the comment above promises will
           not happen.

           It matters most under bundled pricing: the meal is billed flat on
           base_occupancy x rooms, so the old `numAdults x nights` shrank and grew
           independently of the charge and the preview discount moved with occupancy
           while the charge stood still. 2,800 / 350 correctly yields 8 either way. */
        const qtyOf = (charge: number | undefined, rate: number, fallback: number) =>
          rate > 0 ? (charge ?? 0) / rate : fallback;

        const room = num(e.room);
        if (room != null && room !== line.ratePerRoom) {
          adjTotal += (room - line.ratePerRoom)
            * qtyOf(line.totalRoomCharges, line.ratePerRoom, line.numberOfRooms * nights);
        }
        const meal = num(e.meal);
        const mealRate = line.mealAdultRate ?? 0;
        if (meal != null && meal !== mealRate) {
          adjTotal += (meal - mealRate)
            * qtyOf(line.totalMealCharges, mealRate, line.numAdults * nights);
        }
        const extra = num(e.extra);
        const extraRate = line.extraAdultRate ?? 0;
        if (extra != null && extraRate > 0 && extra !== extraRate) {
          adjTotal += (extra - extraRate) * qtyOf(line.totalExtraCharges, extraRate, 0);
        }
      });
    }
    const bv = num(bookingValue);
    if (bv != null && bv !== 0) {
      adjTotal += bookingType === 'Percentage' ? (subtotal * bv) / 100 : bv;
    }
    adjTotal = Math.round(adjTotal * 100) / 100;
    const oldAdj = booking.adjustmentTotal ?? 0;
    const oldFinal = booking.finalGrandTotal ?? booking.grandTotal ?? 0;
    // Overall discount: stored figure unless the editor was touched.
    const oldDiscount = booking.discountAmount ?? 0;
    let newDiscount = oldDiscount;
    if (discTouched && !booking.isComplimentary) {
      const dv = num(discValue);
      newDiscount = dv == null || dv <= 0 || discType === '' ? 0
        : discType === 'Percentage'
          ? Math.min((subtotal * dv) / 100, subtotal)
          : Math.min(dv, subtotal);
    }
    // Complimentary waives the selling subtotal — the net stays waived
    // whatever the adjustments say; only the discount figure moves.
    const newFinal = booking.isComplimentary
      ? oldFinal
      : oldFinal - oldAdj + adjTotal + oldDiscount - newDiscount;
    return { adjTotal, oldFinal, newFinal: Math.max(0, newFinal), newDiscount };
  }, [full, booking, lineEdits, bookingType, bookingValue, subtotal, nights, isPerPax,
      discTouched, discType, discValue]);

  // ── Build the replacement adjustment set from the edits ─────────────────
  const buildPayload = useCallback((): BookingAdjustmentRequest[] | string => {
    if (!full) return 'Booking not loaded.';
    const rows: BookingAdjustmentRequest[] = [];

    if (!isPerPax) {
      for (let i = 0; i < full.detailLines.length; i++) {
        const line = full.detailLines[i];
        const e = lineEdits[i];
        if (!e) continue;
        const push = (code: string, agreed: number | null, rack: number) => {
          if (agreed == null || agreed === rack) return false;
          rows.push({
            componentCode: code, adjustmentType: 'AgreedRate',
            detailLineIndex: i, agreedUnitRate: agreed,
            remarks: e.remarks.trim() || undefined,
          });
          return agreed < rack;
        };
        const discounts = [
          push('ROOM', num(e.room), line.ratePerRoom),
          push('MEAL', num(e.meal), line.mealAdultRate ?? 0),
          (line.extraAdultRate ?? 0) > 0
            ? push('EXTRA_PERSON', num(e.extra), line.extraAdultRate ?? 0)
            : false,
        ];
        if (discounts.some(Boolean) && !e.remarks.trim()) {
          return `Line ${i + 1} (${line.categoryName ?? 'category'}): a reason is required for a discount.`;
        }
      }
    }

    const bv = num(bookingValue);
    if (bv != null && bv !== 0) {
      if (bv < 0 && !bookingRemarks.trim()) {
        return 'A reason is required for the booking-level discount.';
      }
      rows.push({
        componentCode: 'BOOKING', adjustmentType: bookingType,
        adjustmentValue: bv, remarks: bookingRemarks.trim() || undefined,
      });
    }
    return rows;
  }, [full, isPerPax, lineEdits, bookingType, bookingValue, bookingRemarks]);

  /** Prefill the overall-discount editor with the booked-vs-arrived gap. */
  const applyGapAsDiscount = useCallback(() => {
    if (!actualsRecalc || actualsRecalc.gap <= 0) return;
    setDiscType('FlatAmount');
    setDiscValue(String(actualsRecalc.gap.toFixed(2)));
    setDiscRemarks(
      `Charged at actuals: booked rack ${actualsRecalc.bookedRack.toFixed(2)} vs arrived ${actualsRecalc.actualRack.toFixed(2)}.`);
    setDiscTouched(true);
  }, [actualsRecalc]);

  const handleSave = useCallback(async (resolveAfter: boolean) => {
    if (!approval?.bookingId || !full) return;
    const payload = buildPayload();
    if (typeof payload === 'string') { setError(payload); return; }
    // Overall-discount override — only when the editor was touched.
    let discount: RateWorksheetDiscount | undefined;
    if (discTouched && !booking?.isComplimentary) {
      const dv = num(discValue);
      if (dv != null && dv > 0 && discType === '') {
        setError('Pick a discount type (percentage or flat) for the overall discount.');
        return;
      }
      if (dv != null && dv > 0 && !discRemarks.trim()) {
        setError('A reason is required for the overall discount.');
        return;
      }
      discount = {
        discountType: dv != null && dv > 0 ? discType : undefined,
        discountValue: dv != null && dv > 0 ? dv : undefined,
        discountRemarks: discRemarks.trim() || undefined,
      };
    }
    setError('');
    setSaving(true);
    try {
      await bookingService.applyRateWorksheet(approval.bookingId, payload, discount);
      if (resolveAfter) {
        try {
          // The booking was just re-priced: settle the request as REPRICED (the
          // server checks the booking's re-priced stamp is newer than the request).
          await approvalService.approve(approval.approvalRequestId, {
            notes: responseNote.trim() || undefined,
            rowVersion: approval.rowVersion,
            resolution: 'REPRICED',
          });
          notificationService.toast.success('Rates updated and the item resolved.');
        } catch {
          // The pricing IS saved — only the closure failed. Keep the item in
          // the queue rather than pretending nothing happened.
          notificationService.toast.warning(
            'Rates saved, but resolving the item failed — resolve it from the list.');
        }
      } else {
        notificationService.toast.success('Rates updated.');
      }
      onApplied();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Rate update failed.';
      notificationService.toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [approval, full, booking, buildPayload, responseNote, onApplied, onClose,
      discTouched, discType, discValue, discRemarks]);

  const updateLine = useCallback((i: number, patch: Partial<LineEdit>) => {
    setLineEdits((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  }, []);

  if (!isOpen || !approval) return null;

  const isPending = approval.approvalStatusCode === 'APPROVAL_PENDING';

  return (
    <>
      <div className="modal-backdrop" />
      {/* No inline position — .modal-container centres itself via position:fixed
          + translate(-50%,-50%); overriding it drops the modal into document
          flow (it rendered at the bottom of the page). The class already
          provides the flex column, max-height and overflow. */}
      <div className="modal-container modal-lg">
        {(loading || saving) && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {saving ? 'Saving…' : 'Loading booking…'}
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Rate Worksheet — {approval.bookingNumber || '—'}
          </h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflowY: 'auto' }}>
          {/* The request being settled — the worksheet exists because of it. */}
          <div style={{
            border: '1px solid rgba(217,70,239,.35)', background: 'rgba(217,70,239,.06)',
            borderRadius: '7px', padding: '0.5rem 0.7rem', marginBottom: '0.75rem',
            fontSize: '0.8rem', color: 'var(--text-primary)',
          }}>
            <span style={{ fontWeight: 700, fontSize: '0.68rem', color: '#86198f' }}>
              {approval.approvalType === 'FO_CHANGE_REQUEST' ? 'FO REQUEST' : 'ARRIVAL CHANGE'}
              {' · '}{approval.requestedByName || 'Front office'}
              {booking?.bookingStatusCode === 'CHECKED_OUT' ? ' · GUEST DEPARTED' : ''}
            </span>
            <div style={{ fontStyle: 'italic', marginTop: '0.15rem' }}>
              “{approval.justification || '—'}”
            </div>
            {approval.requestedAmount != null && (() => {
              // The desk's target next to what this worksheet will make the total.
              const target = approval.requestedBasis === 'PER_NIGHT'
                ? null
                : Number(approval.requestedAmount);
              const newTotal = estimate?.newFinal ?? approval.bookingCurrentTotal ?? 0;
              const gap = target != null && estimate ? newTotal - target : null;
              return (
                <div style={{ marginTop: '0.3rem', fontSize: '0.78rem' }}>
                  Asked for <b>{formatCurrency(Number(approval.requestedAmount))}</b> {requestedBasisLabel(approval.requestedBasis)}
                  {' · '}new total <b>{formatCurrency(newTotal)}</b>
                  {gap != null && Math.abs(gap) >= 0.01 && (
                    <span style={{ color: '#b45309' }}> ({gap > 0 ? '+' : '−'}{formatCurrency(Math.abs(gap))} from the request)</span>
                  )}
                  {gap != null && Math.abs(gap) < 0.01 && <span style={{ color: '#047857' }}> — matches the request</span>}
                </div>
              );
            })()}
          </div>

          {error && <div className="form-error-message" style={{ marginBottom: '0.6rem' }}>{error}</div>}

          {/* ── Checked in — ACTUALS. What actually happened at the desk,
                 next to what was booked: the facts the executive is pricing. ── */}
          {checkIn && (
            <div style={{
              border: '1px solid var(--border-color)', borderRadius: '7px',
              padding: '0.55rem 0.75rem', marginBottom: '0.6rem', background: 'var(--bg-secondary)',
            }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
                Checked in — actuals
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                <span>
                  <b>{checkIn.rooms.length}</b> room{checkIn.rooms.length !== 1 ? 's' : ''} arrived
                  <span style={{ color: 'var(--text-muted)' }}> (booked {booking?.totalRooms ?? '—'})</span>
                </span>
                <span>
                  <b>{checkIn.numAdults ?? '—'}</b>A
                  {(checkIn.numChildren ?? 0) > 0 ? <> + <b>{checkIn.numChildren}</b>C</> : null}
                  {(checkIn.numInfants ?? 0) > 0 ? <> + <b>{checkIn.numInfants}</b> inf</> : null}
                  <span style={{ color: 'var(--text-muted)' }}> (booked {booking?.numAdults ?? '—'}A{(booking?.numChildren ?? 0) > 0 ? ` + ${booking?.numChildren}C` : ''})</span>
                </span>
                <span style={{ color: 'var(--text-muted)' }}>
                  Arrived {checkIn.checkInDate?.slice(0, 10)} {String(checkIn.checkInTime ?? '').slice(0, 5)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                {checkIn.rooms.map((r) => (
                  <span key={r.id} style={{
                    fontSize: '0.7rem', fontWeight: 600, padding: '0.1rem 0.45rem',
                    border: '1px solid var(--form-border-color)', borderRadius: '5px',
                    background: 'var(--bg-primary)',
                  }}>
                    {r.roomNumber}{r.categoryCode ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> {r.categoryCode}</span> : null}
                  </span>
                ))}
              </div>
              {actualsRecalc && Math.abs(actualsRecalc.gap) >= 0.01 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.45rem', paddingTop: '0.4rem',
                  borderTop: '1px dashed var(--border-color)', fontSize: '0.78rem',
                }}>
                  <span>
                    Rack at booked <b>{formatCurrency(actualsRecalc.bookedRack)}</b>
                    {' · '}at actuals <b>{formatCurrency(actualsRecalc.actualRack)}</b>
                    {' · '}gap{' '}
                    <b style={{ color: actualsRecalc.gap > 0 ? '#b45309' : '#047857' }}>
                      {actualsRecalc.gap > 0 ? '−' : '+'}{formatCurrency(Math.abs(actualsRecalc.gap))}
                    </b>
                    <span style={{ display: 'block', fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                      Rooms exact per line; meals scaled by on-site adults ({actualsRecalc.actualAdults} vs {actualsRecalc.bookedAdults} booked) — advisory, never auto-applied.
                    </span>
                  </span>
                  {actualsRecalc.gap > 0 && !booking?.isComplimentary && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={applyGapAsDiscount}
                      disabled={saving}
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem' }}
                    >
                      Apply gap as overall discount
                    </button>
                  )}
                </div>
              )}
              {totalNoShow > 0 && (
                <div style={{
                  marginTop: '0.45rem', paddingTop: '0.4rem',
                  borderTop: '1px dashed var(--border-color)', fontSize: '0.78rem',
                }}>
                  <b style={{ color: '#b91c1c' }}>{totalNoShow} room{totalNoShow !== 1 ? 's' : ''} no-show</b>
                  {noShowPolicyLabel && (
                    <> · property policy: <b>{noShowPolicyLabel}</b></>
                  )}
                  <span style={{ display: 'block', fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                    The policy guides — you decide: honour the booked rate, apply the policy
                    via the rates or overall discount below, or waive with the gap button.
                  </span>
                </div>
              )}
            </div>
          )}

          {isPerPax && (
            <div style={{
              padding: '0.5rem 0.75rem', marginBottom: '0.6rem', fontSize: '0.78rem',
              background: 'var(--bg-secondary)', border: '1px dashed var(--border-color)',
              borderRadius: '6px', color: 'var(--text-secondary)',
            }}>
              This booking is priced <b>per pax</b> — express any concession as a
              booking-level flat amount or percentage below.
            </div>
          )}

          {/* ── Per-line components: rack vs agreed, same vocabulary as the
                 booking form's adjustment panel ── */}
          {!isPerPax && full?.detailLines.map((line, i) => {
            const e = lineEdits[i];
            if (!e) return null;
            const extraRate = line.extraAdultRate ?? 0;
            const rows: Array<[string, string, number, string, (v: string) => void]> = [
              ['Room Tariff', 'room-night', line.ratePerRoom, e.room, (v) => updateLine(i, { room: v })],
              ['Meal Plan', 'adult-night', line.mealAdultRate ?? 0, e.meal, (v) => updateLine(i, { meal: v })],
            ];
            if (extraRate > 0) {
              rows.push(['Extra Person', 'person-night', extraRate, e.extra, (v) => updateLine(i, { extra: v })]);
            }
            return (
              <div key={line.bookingDetailId} style={{
                border: '1px solid var(--border-color)', borderRadius: '7px',
                padding: '0.6rem 0.75rem', marginBottom: '0.6rem',
              }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.4rem', display: 'flex', alignItems: 'baseline', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {line.categoryName ?? 'Category'}
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                    · {line.numberOfRooms} room{line.numberOfRooms !== 1 ? 's' : ''} · {nights} night{nights !== 1 ? 's' : ''}
                  </span>
                  {/* Plan chip — this is the CURRENT plan, i.e. it already
                      reflects an FO arrival change (CP → CPAI etc.), and the
                      Meal Plan row below prices at ITS rack rate. */}
                  {line.mealPlanCode && (
                    <span style={{
                      fontSize: '0.66rem', fontWeight: 700, padding: '0.05rem 0.45rem',
                      borderRadius: '999px', background: 'rgba(16,185,129,.1)',
                      color: '#047857', border: '1px solid rgba(16,185,129,.35)',
                    }}>
                      {line.mealPlanCode}
                    </span>
                  )}
                  {arrivedByLine && arrivedByLine[i] !== line.numberOfRooms && (
                    <span style={{
                      fontSize: '0.66rem', fontWeight: 700, padding: '0.05rem 0.45rem',
                      borderRadius: '999px', background: 'rgba(245,158,11,.12)',
                      color: '#b45309', border: '1px solid rgba(245,158,11,.4)',
                    }}>
                      {arrivedByLine[i]} of {line.numberOfRooms} arrived
                    </span>
                  )}
                  {(line.noShowRooms ?? 0) > 0 && (
                    <span style={{
                      fontSize: '0.66rem', fontWeight: 700, padding: '0.05rem 0.45rem',
                      borderRadius: '999px', background: 'rgba(239,68,68,.1)',
                      color: '#b91c1c', border: '1px solid rgba(239,68,68,.35)',
                    }}>
                      {line.noShowRooms} no-show
                    </span>
                  )}
                </div>
                {rows.map(([label, unit, rack, val, set]) => (
                  <div key={label} style={{
                    display: 'grid', gridTemplateColumns: 'minmax(120px,1.4fr) 110px 110px',
                    gap: '0.5rem', alignItems: 'center', marginBottom: '0.3rem', fontSize: '0.8rem',
                  }}>
                    <span>{label} <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}>/ {unit}</span></span>
                    <span style={{ textAlign: 'right', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(rack)}
                    </span>
                    <input
                      type="number" min={0} step="0.01"
                      className="form-input"
                      value={val}
                      placeholder={String(rack)}
                      onChange={(ev) => set(ev.target.value)}
                      disabled={saving}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', textAlign: 'right' }}
                    />
                  </div>
                ))}
                <input
                  type="text" maxLength={200}
                  className="form-input"
                  value={e.remarks}
                  onChange={(ev) => updateLine(i, { remarks: ev.target.value })}
                  placeholder="Reason — required for a discount on this line"
                  disabled={saving}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginTop: '0.2rem' }}
                />
              </div>
            );
          })}

          {/* ── Booking-level concession ── */}
          <div style={{
            border: '1px solid var(--border-color)', borderRadius: '7px',
            padding: '0.6rem 0.75rem', marginBottom: '0.6rem',
          }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.4rem' }}>
              Booking-level adjustment
              <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · negative = discount, positive = premium</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                className="form-select"
                value={bookingType}
                onChange={(e) => setBookingType(e.target.value as 'Percentage' | 'FlatAmount')}
                disabled={saving}
                style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', width: 'auto' }}
              >
                <option value="FlatAmount">Flat amount</option>
                <option value="Percentage">Percent of subtotal</option>
              </select>
              <input
                type="number" step="0.01"
                className="form-input"
                value={bookingValue}
                onChange={(e) => setBookingValue(e.target.value)}
                placeholder="0"
                disabled={saving}
                style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', width: '110px', textAlign: 'right' }}
              />
              <input
                type="text" maxLength={200}
                className="form-input"
                value={bookingRemarks}
                onChange={(e) => setBookingRemarks(e.target.value)}
                placeholder="Reason — required for a discount"
                disabled={saving}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1, minWidth: '160px' }}
              />
            </div>
          </div>

          {/* ── Overall discount — editable (worksheet override). Distinct from
                 the booking-level ADJUSTMENT above: the adjustment is a signed
                 selling-price component; this is the booking's discount field
                 the guest sees on the bill. Hidden for complimentary (waiver
                 is a booking-level state, not a worksheet decision). ── */}
          {!booking?.isComplimentary && (
            <div style={{
              border: '1px solid var(--border-color)', borderRadius: '7px',
              padding: '0.6rem 0.75rem', marginBottom: '0.6rem',
            }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.4rem' }}>
                Overall discount
                <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · leave untouched to keep the stored discount</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  className="form-select"
                  value={discType}
                  onChange={(e) => { setDiscType(e.target.value as '' | 'Percentage' | 'FlatAmount'); setDiscTouched(true); }}
                  disabled={saving}
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', width: 'auto' }}
                >
                  <option value="">No discount</option>
                  <option value="Percentage">Percent of subtotal</option>
                  <option value="FlatAmount">Flat amount</option>
                </select>
                <input
                  type="number" min={0} step="0.01"
                  className="form-input"
                  value={discValue}
                  onChange={(e) => { setDiscValue(e.target.value); setDiscTouched(true); }}
                  placeholder="0"
                  disabled={saving || discType === ''}
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', width: '110px', textAlign: 'right' }}
                />
                <input
                  type="text" maxLength={200}
                  className="form-input"
                  value={discRemarks}
                  onChange={(e) => { setDiscRemarks(e.target.value); setDiscTouched(true); }}
                  placeholder="Reason — required for a discount"
                  disabled={saving}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1, minWidth: '160px' }}
                />
              </div>
            </div>
          )}

          {/* ── Delta ladder ── */}
          {estimate && booking && (
            <div style={{
              marginLeft: 'auto', maxWidth: '340px', fontSize: '0.82rem',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Adjustment total</span>
                <span style={{ color: estimate.adjTotal < 0 ? '#b91c1c' : 'var(--text-primary)' }}>
                  {estimate.adjTotal < 0 ? '−' : ''}{formatCurrency(Math.abs(estimate.adjTotal))}
                </span>
              </div>
              {discTouched && !booking.isComplimentary && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Overall discount</span>
                  <span style={{ color: estimate.newDiscount > 0 ? '#b91c1c' : 'var(--text-primary)' }}>
                    {estimate.newDiscount > 0 ? `−${formatCurrency(estimate.newDiscount)}` : '—'}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Current total</span>
                <span>{formatCurrency(estimate.oldFinal)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0 0.1rem',
                borderTop: '2px solid var(--text-primary)', fontWeight: 700, fontSize: '0.9rem',
              }}>
                <span>New total{booking.isComplimentary ? ' (complimentary)' : ''}</span>
                <span>{formatCurrency(estimate.newFinal)}</span>
              </div>
              <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                Estimate — the server recomputes with the same formulas on save.
              </div>
            </div>
          )}

          {/* Response note — travels back to the desk's thread on Save & Resolve. */}
          {canApprove && isPending && (
            <div className="form-group" style={{ marginTop: '0.6rem', marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>
                Response to the front office (sent with Save &amp; Resolve)
              </label>
              <textarea
                className="form-input form-textarea"
                rows={2}
                maxLength={1000}
                value={responseNote}
                onChange={(e) => setResponseNote(e.target.value)}
                placeholder="What was done — shown on the check-in screen's request thread…"
                disabled={saving}
                style={{ fontSize: '0.8rem' }}
              />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-secondary"
            onClick={() => handleSave(false)}
            disabled={saving || loading || !full}
            title="Apply the rates without closing the change item"
          >
            Save
          </button>
          {canApprove && isPending && (
            <button
              className="btn btn-primary"
              onClick={() => handleSave(true)}
              disabled={saving || loading || !full}
              title="Apply the rates and mark the change item resolved"
            >
              <IconSystem.check size={16} /> Save &amp; Resolve
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default RateWorksheetModal;
